import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { CoverageControl } from './CoverageControl';
import { DeliveryModeToggles } from './DeliveryModeToggles';
import type {
  CoverageScope,
  ServiceRange,
  SpecialtyCoverageItem,
  SpecialtyCoverageState,
} from './types';

type SpecialtyCoverageRowProps = {
  item: SpecialtyCoverageItem;
  name: string;
  canManageAttachments: boolean;
  fieldError?: string;
  disabled?: boolean;
  onCoverageChange: (scope: CoverageScope, states: SpecialtyCoverageState[]) => void;
  onToggleMode: (mode: ServiceRange) => void;
  onRequestRemove: () => void;
  onRequestBulkCopy: () => void;
};

export function SpecialtyCoverageRow({
  item,
  name,
  canManageAttachments,
  fieldError,
  disabled = false,
  onCoverageChange,
  onToggleMode,
  onRequestRemove,
  onRequestBulkCopy,
}: SpecialtyCoverageRowProps) {
  return (
    <div
      data-specialty-id={item.specialtyId}
      className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-4"
    >
      <div className="min-w-48">
        <p className="font-medium text-text-primary">{name}</p>
        <CoverageControl
          specialtyName={name}
          coverageScope={item.coverageScope}
          coverageStates={item.coverageStates}
          onChange={onCoverageChange}
          disabled={disabled}
        />
        {fieldError ? (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {fieldError}
          </p>
        ) : null}
      </div>

      <div className="ml-auto flex items-center gap-1">
        <DeliveryModeToggles
          modes={item.serviceRanges}
          onToggle={onToggleMode}
          disabled={disabled}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Specialty actions"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-card-subtle hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage"
              disabled={disabled}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => {
                // Defer so Radix menu unmounts before dialog focus trap (jsdom).
                setTimeout(onRequestBulkCopy, 0);
              }}
            >
              Copy coverage to…
            </DropdownMenuItem>
            {canManageAttachments ? (
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600"
                onSelect={() => {
                  setTimeout(onRequestRemove, 0);
                }}
              >
                Remove specialty
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
